import type {
  OntologyGraphLayer,
  OntologyGraphTree,
  OPMGraphData,
  OntologyGraphPayload,
} from '../../../types/opm';

export const DEFAULT_LAYER_NAME = '本体图';

const SAFE_EMPTY_GRAPH: OPMGraphData = { nodes: [], edges: [], opl: [] };

// 浅拷贝节点（保留 properties 引用，因为通常不会修改）
const shallowCloneNode = (node: any): any => ({
  ...node,
  // properties 通常是只读的，但如果有修改需求可以浅拷贝
  properties: node.properties ? { ...node.properties } : undefined,
  // text 可能被修改，浅拷贝
  text: node.text ? { ...node.text } : undefined,
});

// 浅拷贝边
const shallowCloneEdge = (edge: any): any => ({
  ...edge,
  properties: edge.properties ? { ...edge.properties } : undefined,
  text: edge.text ? { ...edge.text } : undefined,
});

// 性能优化：使用浅拷贝替代 JSON.parse(JSON.stringify())
// 原来的深拷贝在5000+节点时耗时2000ms+，浅拷贝只需<50ms
export const cloneGraphData = (graphData?: OPMGraphData): OPMGraphData => {
  if (!graphData) return { nodes: [], edges: [] };

  return {
    nodes: graphData.nodes.map(shallowCloneNode),
    edges: graphData.edges.map(shallowCloneEdge),
    opl: graphData.opl ? [...graphData.opl] : undefined,
  };
};

// 保留原始的深拷贝方法，以备必要时使用
const deepClone = <T>(value: T): T =>
  value ? JSON.parse(JSON.stringify(value)) : (value as T);

export const deepCloneGraphData = (graphData?: OPMGraphData): OPMGraphData =>
  deepClone(graphData || SAFE_EMPTY_GRAPH);

export const isGraphTreePayload = (
  payload: unknown
): payload is OntologyGraphTree => {
  if (!Array.isArray(payload)) return false;
  return payload.every(
    layer =>
      layer &&
      typeof layer === 'object' &&
      'graphData' in layer &&
      Array.isArray((layer as OntologyGraphLayer).graphData?.nodes) &&
      Array.isArray((layer as OntologyGraphLayer).graphData?.edges)
  );
};

const isGraphPayload = (payload: unknown): payload is OntologyGraphPayload => {
  if (!payload || typeof payload !== 'object') return false;
  return (
    (payload as OntologyGraphPayload).version === 'v2' &&
    Array.isArray((payload as OntologyGraphPayload).data)
  );
};

const deriveLayerId = (parentId: string, index: number): string => {
  if (!parentId) {
    return `${index + 1}`;
  }
  return `${parentId}-${index + 1}`;
};

const ensureLayerDefaults = (
  layers: OntologyGraphTree,
  parentId: string = ''
): OntologyGraphTree => {
  return layers.map((layer, index) => {
    const nextLayerId =
      layer.layerId && layer.layerId.trim().length > 0
        ? layer.layerId
        : deriveLayerId(parentId, index);

    const nextLayerName =
      layer.layerName && layer.layerName.trim().length > 0
        ? layer.layerName
        : parentId
          ? `${DEFAULT_LAYER_NAME}-${index + 1}`
          : DEFAULT_LAYER_NAME;

    const normalizedChildren = ensureLayerDefaults(
      layer.children || [],
      nextLayerId
    );

    return {
      layerId: nextLayerId,
      layerName: nextLayerName,
      graphData: cloneGraphData(layer.graphData),
      children: normalizedChildren,
      parentLayerId: parentId || undefined,
      parentNodeId: layer.parentNodeId,
      nodeIdPrefix: layer.nodeIdPrefix || '',
    };
  });
};

export const createEmptyLayer = (
  overrides: Partial<OntologyGraphLayer> = {}
): OntologyGraphLayer => ({
  layerId: overrides.layerId || '1',
  layerName: overrides.layerName || DEFAULT_LAYER_NAME,
  graphData: cloneGraphData(overrides.graphData),
  children: overrides.children ? ensureLayerDefaults(overrides.children) : [],
  parentLayerId: overrides.parentLayerId,
  parentNodeId: overrides.parentNodeId,
  nodeIdPrefix: overrides.nodeIdPrefix,
});

export const convertLegacyGraphToTree = (
  graphData?: OPMGraphData
): OntologyGraphTree => [
  createEmptyLayer({
    graphData: graphData || SAFE_EMPTY_GRAPH,
  }),
];

export const normalizeGraphTree = (
  payload?: OntologyGraphTree | OPMGraphData | OntologyGraphPayload
): OntologyGraphTree => {
  if (!payload) {
    return convertLegacyGraphToTree();
  }

  if (isGraphPayload(payload)) {
    return ensureLayerDefaults(payload.data || []);
  }

  if (isGraphTreePayload(payload)) {
    return ensureLayerDefaults(payload);
  }

  return convertLegacyGraphToTree(payload as OPMGraphData);
};

export const createGraphPayload = (
  tree: OntologyGraphTree
): OntologyGraphPayload => ({
  version: 'v2',
  data: tree,
});

export const findLayerById = (
  layers: OntologyGraphTree,
  layerId: string
): OntologyGraphLayer | undefined => {
  for (const layer of layers) {
    if (layer.layerId === layerId) return layer;
    if (layer.children?.length) {
      const found = findLayerById(layer.children, layerId);
      if (found) return found;
    }
  }
  return undefined;
};

export const updateLayerById = (
  layers: OntologyGraphTree,
  layerId: string,
  updater: (layer: OntologyGraphLayer) => OntologyGraphLayer
): OntologyGraphTree => {
  return layers.map(layer => {
    if (layer.layerId === layerId) {
      const updatedLayer = updater(layer);
      return {
        ...updatedLayer,
        children: updatedLayer.children || [],
      };
    }

    if (layer.children?.length) {
      return {
        ...layer,
        children: updateLayerById(layer.children, layerId, updater),
      };
    }

    return layer;
  });
};

export const buildLayerMap = (
  layers: OntologyGraphTree,
  map: Map<string, OntologyGraphLayer> = new Map()
): Map<string, OntologyGraphLayer> => {
  layers.forEach(layer => {
    map.set(layer.layerId, layer);
    if (layer.children?.length) {
      buildLayerMap(layer.children, map);
    }
  });
  return map;
};

export const getLayerPathNames = (
  layers: OntologyGraphTree,
  targetId: string,
  path: string[] = []
): string[] => {
  for (const layer of layers) {
    const nextPath = [...path, layer.layerName];
    if (layer.layerId === targetId) return nextPath;
    if (layer.children?.length) {
      const result = getLayerPathNames(layer.children, targetId, nextPath);
      if (result.length) return result;
    }
  }
  return [];
};

export const generateChildLayerMeta = (
  parentLayer: OntologyGraphLayer
): { layerId: string; layerName: string } => {
  const childIndex = (parentLayer.children?.length || 0) + 1;
  const layerId = `${parentLayer.layerId}-${childIndex}`;
  const layerName =
    parentLayer.layerName === DEFAULT_LAYER_NAME
      ? `${DEFAULT_LAYER_NAME}${childIndex}`
      : `${parentLayer.layerName}-${childIndex}`;

  return { layerId, layerName };
};

export const appendChildLayer = (
  layers: OntologyGraphTree,
  parentLayerId: string,
  childLayer: OntologyGraphLayer
): OntologyGraphTree => {
  return updateLayerById(layers, parentLayerId, layer => ({
    ...layer,
    children: [...(layer.children || []), childLayer],
  }));
};

export const updateNodeInLayer = (
  layers: OntologyGraphTree,
  targetLayerId: string,
  nodeId: string,
  updater: (node: any) => any
): OntologyGraphTree => {
  return updateLayerById(layers, targetLayerId, layer => {
    const nodes = layer.graphData?.nodes || [];
    const updatedNodes = nodes.map(node =>
      node.id === nodeId ? updater(node) : node
    );
    return {
      ...layer,
      graphData: {
        ...layer.graphData,
        nodes: updatedNodes,
        edges: layer.graphData?.edges || [],
      },
    };
  });
};

export const collectChildLayersByNodeId = (
  layers: OntologyGraphTree,
  nodeId: string,
  acc: OntologyGraphLayer[] = []
): OntologyGraphLayer[] => {
  layers.forEach(layer => {
    if (layer.parentNodeId === nodeId) {
      acc.push(layer);
    }
    if (layer.children?.length) {
      collectChildLayersByNodeId(layer.children, nodeId, acc);
    }
  });
  return acc;
};

export const removeLayersByNodeId = (
  layers: OntologyGraphTree,
  nodeId: string
): OntologyGraphTree => {
  return layers
    .filter(layer => layer.parentNodeId !== nodeId)
    .map(layer => ({
      ...layer,
      children: layer.children?.length
        ? removeLayersByNodeId(layer.children, nodeId)
        : [],
    }));
};
